import { computed, unref, type MaybeRef } from "vue";

/**
 * The fields the strike-through price is read from, on any product shape —
 * PDP product, listing hit or basket line (whose `PriceStandard` may be null).
 */
interface PricedProduct {
	VatRate?: number;
	PriceStandard?: number | null;
	PriceIncVat?: number;
	OldPrice?: string;
}

/**
 * The price a product's page strikes through, or `null` when it has none.
 * Pure, so code outside a component (the PDP data loader) can read it without
 * building a computed.
 */
export function getOldPrice(
	resolvedProduct: PricedProduct | null | undefined
): number | null {
	if (!resolvedProduct) return null;

	let calculatedValue: number | null = null;

	if (resolvedProduct.VatRate) {
		calculatedValue = Number(
			(resolvedProduct.PriceStandard! * resolvedProduct.VatRate).toFixed(2)
		);
	}

	if (resolvedProduct.OldPrice) {
		calculatedValue = Number(resolvedProduct.OldPrice);
	}

	if (calculatedValue === null) return null;

	/*
		The two are the same price whenever they are within a rounding step of
		each other, not only when they are identical.

		`PriceStandard` reaches us already rounded to the cent, so the figure
		behind it lies anywhere in a half-cent band; multiplying by the VAT rate
		widens that, and rounding our own result widens it again. A product at
		10.38 ex VAT and a rate of 1.2 gives 12.456, which we round to 12.46,
		while Norce reports 12.45 — both right, rounded from different points.
		Compared for equality that reads as a one-cent discount, and the page
		strikes through a price the shopper is actually paying.

		A cent is the whole of the error and nothing a real campaign would ever
		be, so anything inside it is rounding rather than a saving.
	*/
	const roundingStep = 0.011;

	return Math.abs(calculatedValue - (resolvedProduct.PriceIncVat ?? 0)) <
		roundingStep
		? null
		: calculatedValue;
}

export function useProductPrices(
	product: MaybeRef<PricedProduct | null | undefined>
) {
	const oldPrice = computed(() => getOldPrice(unref(product)));

	return {
		oldPrice
	};
}
